import React, { useState, useMemo, useEffect } from 'react';
import { 
  ChevronLeft, 
  ChevronRight, 
  Search, 
  Filter, 
  AlertTriangle, 
  Activity, 
  Clock, 
  X,
  MapPin, 
  Plus,
  Loader2,
  Calendar as CalendarIcon,
  Zap,
  DollarSign,
  Fingerprint,
  Users,
  PieChart,
  BarChart,
  ArrowRight
} from 'lucide-react';
import { 
  format, 
  addMonths, 
  subMonths, 
  startOfMonth, 
  endOfMonth, 
  startOfWeek, 
  endOfWeek, 
  isSameMonth, 
  isSameDay, 
  eachDayOfInterval, 
  differenceInDays, 
  addDays,
  parseISO, 
  isValid,
  isAfter,
  areIntervalsOverlapping,
  isWithinInterval // Added missing import
} from 'date-fns';

import { encryptedFetch as fetch } from '../security'; 
import { SimulatedCampaignSummary } from './SimulatedCampaignSummary';

// ====================================================================================
// CONFIGURATION
// ====================================================================================

const CAMPAIGN_FOCUS_TYPES = {
  VOTER_TURNOUT: { label: "Voter Turnout", color: "bg-emerald-500", text: "text-emerald-400", border: "border-emerald-500", shadow: "shadow-emerald-500/20" },
  SWING_VOTER_CONVERSION: { label: "Swing Conversion", color: "bg-blue-500", text: "text-blue-400", border: "border-blue-500", shadow: "shadow-blue-500/20" },
  CORE_SUPPORT_MOBILIZATION: { label: "Core Mobilization", color: "bg-red-500", text: "text-red-400", border: "border-red-500", shadow: "shadow-red-500/20" },
  YOUTH_ENGAGEMENT: { label: "Youth Engagement", color: "bg-purple-500", text: "text-purple-400", border: "border-purple-500", shadow: "shadow-purple-500/20" },
  DEFENSIVE_RETENTION: { label: "Defensive Retention", color: "bg-amber-500", text: "text-amber-400", border: "border-amber-500", shadow: "shadow-amber-500/20" },
  TACTICAL_VOTING: { label: "Tactical Voting", color: "bg-orange-500", text: "text-orange-400", border: "border-orange-500", shadow: "shadow-orange-500/20" },
  ISSUE_BASED_PERSUASION: { label: "Issue Persuasion", color: "bg-cyan-500", text: "text-cyan-400", border: "border-cyan-500", shadow: "shadow-cyan-500/20" },
  LATE_DECIDER_CAPTURE: { label: "Late Decider", color: "bg-rose-500", text: "text-rose-400", border: "border-rose-500", shadow: "shadow-rose-500/20" },
};

const CAPACITY_LIMIT_WEEKLY = 150; 

const formatChannelName = (name) => {
    if (!name) return 'N/A';
    return name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
}

// ====================================================================================
// SUB-COMPONENTS
// ====================================================================================

const ResourceHeader = ({ currentMonthData }) => {
  const totalHours = currentMonthData.reduce((acc, c) => acc + (c.total_volunteer_hours || 0), 0);
  const capacity = CAPACITY_LIMIT_WEEKLY * 4; 
  const percent = capacity > 0 ? Math.min((totalHours / capacity) * 100, 100) : 0;
  
  let statusColor = "bg-emerald-500";
  if (percent > 80) statusColor = "bg-amber-500";
  if (percent > 100) statusColor = "bg-red-500";

  return (
    <div className="bg-slate-800/50 border border-slate-700/50 rounded-xl p-4 flex items-center justify-between mb-6 backdrop-blur-sm">
      <div className="flex items-center space-x-4">
        <div className={`p-3 rounded-lg bg-slate-700/50 text-slate-300`}>
          <Activity size={20} />
        </div>
        <div>
          <h4 className="text-sm font-semibold text-slate-200 uppercase tracking-wide">Resource Load (Month)</h4>
          <div className="text-xs text-slate-400">Total Volunteer Hours Booked vs Capacity</div>
        </div>
      </div>
      
      <div className="flex items-center space-x-6">
        <div className="text-right">
          <div className="text-2xl font-bold text-white">{totalHours.toFixed(0)} <span className="text-sm text-slate-500 font-normal">/ {capacity} hrs</span></div>
        </div>
        <div className="w-32 h-2 bg-slate-700 rounded-full overflow-hidden">
          <div 
            className={`h-full ${statusColor} transition-all duration-500`} 
            style={{ width: `${percent}%` }}
          />
        </div>
      </div>
    </div>
  );
};

// --- RICH SIDEBAR (PLATO INTEGRATION) ---
const EventSidebar = ({ event, onClose, setActiveView }) => {

  if (!event) return null;

  const style = CAMPAIGN_FOCUS_TYPES[event.campaign_focus] || CAMPAIGN_FOCUS_TYPES.ISSUE_BASED_PERSUASION;
  
  const summary = event.input_summary || {};
  const optimization = event.campaign_optimization?.optimal_budget_allocation || {};
  const metrics = event.campaign_optimization?.campaign_efficiency_metrics || {};
  const allocations = optimization.allocations || [];
  
  const projectedVotes = optimization.expected_total_votes || 0;
  
  // Find min volunteers
  const recs = event.campaign_optimization?.budget_expansion_opportunities?.recommendations || [];
  const minVolunteers = recs[0]?.min_volunteers_required || 
                        recs[0]?.weekly_schedule?.[Object.keys(recs[0]?.weekly_schedule || {})[0]]?.volunteers_needed || 
                        "N/A";

  const isHybridROI = metrics.gross_votes_per_hour > 0 && metrics.gross_cost_per_vote > 0;
  const fatiguePercent = Math.min(100, Math.max(0, 100 - ((metrics.efficiency_score || 0.8) * 100))); 
  const totalTouchpoints = (metrics.average_touchpoints_per_voter || (summary.budget > 5000 ? 7.2 : 3.5));
  
  let satColor = "text-emerald-400";
  let satLabel = "Optimal Saturation";
  if (totalTouchpoints < 5.0) { satColor = "text-yellow-400"; satLabel = "Low Saturation"; }
  else if (totalTouchpoints > 12.0) { satColor = "text-orange-400"; satLabel = "High Saturation"; }

  return (
    <div className="fixed inset-y-0 right-0 w-[500px] bg-slate-950 border-l border-slate-700 shadow-2xl z-50 transform transition-transform duration-300 ease-in-out flex flex-col">
      
      {/* HEADER SECTION */}
      <div className="flex flex-col border-b border-slate-800 bg-slate-900/50">
          <div className="p-6 pb-4">
            <div className="flex justify-between items-start mb-4">
              <div className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${style.color.replace('bg-', 'bg-opacity-10 ')} ${style.text} ${style.border}`}>
                {style.label}
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors">
                <X size={20} />
              </button>
            </div>
            <h2 className="text-2xl font-bold text-white mb-1 leading-tight">{event.campaign_name}</h2>
            <div className="flex items-center gap-2 text-slate-400 text-xs mt-2">
                <MapPin size={12} />
                <span>{event.ward_id || "Multi-Ward Strategy"}</span>
                <span className="text-slate-600">|</span>
                <CalendarIcon size={12} />
                <span>{isValid(event.start) ? format(event.start, 'MMM d') : 'N/A'} - {isValid(event.end) ? format(event.end, 'MMM d') : 'N/A'}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 border-t border-slate-800 divide-x divide-slate-800 bg-slate-900/80">
              <div className="p-4 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-1">
                      <Zap size={14} className={style.text} />
                      <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Projected Impact</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-white tracking-tight">+{Math.round(projectedVotes).toLocaleString()}</span>
                      <span className="text-[10px] text-slate-500 font-mono">Votes</span>
                  </div>
              </div>

              <div className="p-4 flex flex-col justify-center">
                  <div className="flex items-center gap-2 mb-1">
                      <Users size={14} className="text-blue-400" />
                      <span className="text-[10px] uppercase font-bold text-slate-400 tracking-wider">Min. Volunteers</span>
                  </div>
                  <div className="flex items-baseline gap-1">
                      <span className="text-2xl font-bold text-white tracking-tight">{minVolunteers}</span>
                      <span className="text-[10px] text-slate-500 font-mono">Active</span>
                  </div>
              </div>
          </div>
      </div>

      {/* SCROLLABLE CONTENT */}
      <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          
          <div className={`rounded-xl overflow-hidden border border-slate-800 ${isHybridROI ? 'flex flex-col' : ''}`}>
              {isHybridROI ? (
                  <>
                      <div className="flex-1 px-5 py-3 border-b border-slate-800 bg-slate-900/40">
                          <div className="text-[10px] uppercase tracking-wider text-slate-400 flex items-center justify-between mb-1">
                              <span>Financial Efficiency</span>
                              <DollarSign size={12}/>
                          </div>
                          <div className="text-lg font-bold text-white font-mono">£{(metrics.gross_cost_per_vote || 0).toFixed(2)} / Vote</div>
                      </div>
                      <div className="flex-1 px-5 py-3 bg-slate-900/60">
                          <div className="text-[10px] uppercase tracking-wider text-blue-300 flex items-center justify-between mb-1">
                              <span>Time Efficiency</span>
                              <Clock size={12}/>
                          </div>
                          <div className="text-lg font-bold text-blue-400 font-mono">{(metrics.gross_votes_per_hour || 0).toFixed(1)} Votes / Hr</div>
                      </div>
                  </>
              ) : (
                   <div className="px-5 py-4 bg-slate-900/40">
                        {metrics.gross_votes_per_hour > 0 ? (
                             <>
                                <div className="text-[10px] uppercase tracking-wider text-blue-300 mb-1">Volunteer Velocity</div>
                                <div className="text-xl font-bold text-blue-400 font-mono">{(metrics.gross_votes_per_hour).toFixed(1)} Votes/Hr</div>
                             </>
                        ) : (
                             <>
                                <div className="text-[10px] uppercase tracking-wider text-emerald-300 mb-1">Cost Efficiency</div>
                                <div className="text-xl font-bold text-emerald-400 font-mono">£{(metrics.gross_cost_per_vote || 0).toFixed(2)} CPV</div>
                             </>
                        )}
                   </div>
              )}
          </div>

          <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-800">
                   <div className="flex items-center gap-2 mb-2">
                       <BarChart size={14} className="text-green-400"/>
                       <span className="text-xs font-bold text-white">Strategic Driver</span>
                   </div>
                   <div className="text-sm font-bold text-green-300 mb-1">Balanced</div>
                   <p className="text-[10px] text-slate-400 leading-tight">
                       Model prioritized mix of fit & ROI.
                   </p>
              </div>

              <div className="p-3 bg-slate-900/60 rounded-xl border border-slate-800">
                   <div className="flex items-center gap-2 mb-2">
                       <Activity size={14} className="text-orange-400"/>
                       <span className="text-xs font-bold text-white">Channel Fatigue</span>
                   </div>
                   <div className={`text-sm font-bold mb-1 ${fatiguePercent > 40 ? 'text-orange-400' : 'text-slate-200'}`}>
                       {fatiguePercent.toFixed(0)}%
                   </div>
                   <p className="text-[10px] text-slate-400 leading-tight">
                       {fatiguePercent < 20 ? "Minimal saturation." : "Monitor frequency."}
                   </p>
              </div>
          </div>

          <div className="p-4 bg-slate-900/60 rounded-xl border border-slate-800">
               <div className="flex items-center justify-between mb-3">
                   <div className="flex items-center gap-2">
                       <Fingerprint size={16} className="text-orange-400"/>
                       <span className="text-sm font-bold text-white">Touchpoint Saturation</span>
                   </div>
                   <span className={`text-[10px] font-bold px-2 py-0.5 rounded bg-slate-800 ${satColor}`}>{satLabel}</span>
               </div>
               
               <div className="flex items-end gap-2 mb-1">
                   <span className={`text-3xl font-black ${satColor} font-mono leading-none`}>{totalTouchpoints.toFixed(1)}</span>
                   <span className="text-[10px] text-slate-500 uppercase font-bold mb-1">Avg. Touchpoints / Voter</span>
               </div>
               
               <div className="w-full bg-slate-800 h-1.5 mt-3 rounded-full overflow-hidden">
                   <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500" style={{ width: `${Math.min((totalTouchpoints / 15) * 100, 100)}%` }}></div>
               </div>
          </div>

          <div className="p-4 bg-slate-900/60 rounded-xl border border-slate-800">
              <div className="flex items-center gap-2 mb-4">
                  <PieChart size={16} className="text-emerald-400"/>
                  <h5 className="font-semibold text-white text-sm">Resource Architecture</h5>
              </div>
              
              <div className="space-y-3">
                  {allocations.length > 0 ? allocations.slice(0, 4).map((alloc, idx) => {
                       const isTime = alloc.resource_mode === 'time_only';
                       const isHybrid = alloc.resource_mode === 'hybrid';
                       const barColor = isTime ? 'bg-blue-500' : isHybrid ? 'bg-purple-500' : 'bg-emerald-500';

                       return (
                           <div key={idx}>
                               <div className="flex justify-between items-center text-[10px] mb-1">
                                   <span className="font-medium text-slate-300 truncate w-1/2">{formatChannelName(alloc.channel)}</span>
                                   <div className="text-right font-mono">
                                       {alloc.allocated_budget > 0 && <span className="text-white">£{alloc.allocated_budget.toLocaleString()}</span>}
                                       {alloc.allocated_hours > 0 && <span className="text-blue-300 ml-2">{alloc.allocated_hours.toFixed(0)}h</span>}
                                   </div>
                               </div>
                               <div className="w-full bg-slate-800 rounded-full h-1.5 flex items-center">
                                   <div className={`${barColor} h-1.5 rounded-full`} style={{ width: `${alloc.allocation_percentage ?? 0}%` }}></div>
                               </div>
                           </div>
                       )
                  }) : <p className="text-xs text-slate-500 italic">No allocation data available.</p>}
              </div>
          </div>
      </div>

      <div className="p-6 border-t border-slate-800 bg-slate-900/95 backdrop-blur-sm">
          <button 
             onClick={() => {
                const url = new URL(window.location.href);
                url.searchParams.set('sim', event.analysis_metadata.session_id);
                window.history.pushState({view: "simCalendar"},null,url);
                setActiveView("campaignGallery");
                console.hidden("Event: ", event.analysis_metadata.session_id);
            }}
             className="w-full flex items-center justify-center gap-2 bg-slate-100 hover:bg-white text-slate-900 font-bold py-3 px-4 rounded-xl transition-all duration-200 shadow-lg hover:shadow-xl hover:scale-[1.02]"
          >
             <span>Go to Simulation</span>
             <ArrowRight size={18} />
             
          </button>
      </div>
    </div>
  );
};

// ====================================================================================
// MAIN COMPONENT
// ====================================================================================

export const SimCalendar = ({ campaignId, electionDate = new Date('2025-05-02'), onStartCampaign, setActiveView}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [searchTerm, setSearchTerm] = useState('');
  const [focusFilter, setFocusFilter] = useState('ALL');
  const [wardFilter, setWardFilter] = useState('');

  // --- FETCH LOGIC (ENHANCED FLATTENING WITH WARD NAMES) ---
  useEffect(() => {
    const fetchSimulations = async () => {
        if (!campaignId) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch("https://us-central1-knoesis-dante.cloudfunctions.net/getSimulatedCampaign?campaignID=" + campaignId);
            const data = await response.json();
            
            const flattenedData = Array.isArray(data) ? data.map(sim => {
                // 1. Resolve Campaign Name
                const resolvedName = sim.campaign_name || 
                                     sim.analysis_metadata?.campaign_name || 
                                     sim.input_summary?.campaign_name || 
                                     'Untitled Simulation';

                // 2. Resolve Wards (IDs AND Names)
                const wardData = sim.ward_level_analysis || {};
                const wardKeys = Object.keys(wardData);
                
                // [NEW] Extract human names from the metadata
                const wardNames = Object.values(wardData)
                    .map(w => w.ward_metadata?.ward_name)
                    .filter(Boolean); // Remove nulls/undefined

                const primaryWard = sim.ward_id || (wardKeys.length === 1 ? wardKeys[0] : null);

                return {
                    ...sim,
                    ...sim.input_summary, 
                    id: sim.id || Math.random().toString(36).substr(2, 9),
                    campaign_name: resolvedName,
                    ward_id: primaryWard,       
                    searchable_wards: wardKeys,        // Search by Code (E05...)
                    searchable_ward_names: wardNames   // Search by Name (Horndean...)
                };
            }) : [];

            setCampaigns(flattenedData);
            setLoading(false);
        } catch (err) {
            console.error("Error loading campaigns:", err);
            setLoading(false);
        }
    };

    fetchSimulations();
  }, [campaignId]);

  // --- Derived State: Processed Events ---
  const processedEvents = useMemo(() => {
    if (!campaigns || campaigns.length === 0) return [];

    let events = campaigns.map(c => {
        // [FIX] Priority Check: Look for 'debug_start_date' first as that's what the engine returns for simulated windows
        const rawStart = c.debug_start_date || c.execution_start_date || c.start_date;
        const rawEnd = c.debug_end_date || c.execution_end_date;

        let start = rawStart ? parseISO(rawStart) : new Date();
        let end;

        if (rawEnd) {
            end = parseISO(rawEnd);
        } else if (c.execution_window_days) {
            // Subtract 1 day because if I run for 1 day starting Mon, I end Mon, not Tue.
            const durationOffset = Math.max(0, c.execution_window_days - 1);
            end = addDays(start, durationOffset);
        } else {
            end = addDays(start, 14); // Default duration
        }
        
        // Safety: Ensure end is never before start
        if (isValid(start) && isValid(end) && isAfter(start, end)) end = start; 

        const daysToElection = isValid(end) ? differenceInDays(electionDate, end) : 0;
        const decayWarning = c.is_pulse_campaign && daysToElection > 60;
        const conflictDetected = false; 

        return { ...c, start, end, decayWarning, conflictDetected };
    });

    events = events.filter(e => {
        // [FIX] Robust Name Search
        const nameToSearch = (e.campaign_name || '').toLowerCase();
        const matchesSearch = nameToSearch.includes(searchTerm.toLowerCase());
        
        // [FIX] Strategy Filter
        const matchesFocus = focusFilter === 'ALL' || e.campaign_focus === focusFilter;
        
        // [FIX] Robust Ward Search (Checks Primary ID, List of IDs, AND List of Names)
        const wardSearch = wardFilter.toLowerCase();
        
        const matchesWard = wardFilter === '' || 
                            (e.ward_id || '').toLowerCase().includes(wardSearch) || 
                            (e.searchable_wards || []).some(w => w.toLowerCase().includes(wardSearch)) ||
                            (e.searchable_ward_names || []).some(name => name.toLowerCase().includes(wardSearch));

        return matchesSearch && matchesFocus && matchesWard;
    });

    return events;
  }, [campaigns, searchTerm, focusFilter, wardFilter, electionDate]);

  // --- Calendar Grid Calculation ---
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const calendarDays = eachDayOfInterval({ start: startDate, end: endDate });
  
  const weeks = [];
  let currentWeek = [];
  calendarDays.forEach((day, index) => {
      currentWeek.push(day);
      if ((index + 1) % 7 === 0) {
          weeks.push(currentWeek);
          currentWeek = [];
      }
  });

  const nextMonth = () => setCurrentDate(addMonths(currentDate, 1));
  const prevMonth = () => setCurrentDate(subMonths(currentDate, 1));
  const goToToday = () => setCurrentDate(new Date());

  // --- New Logic: Vertical Stacking Algorithm ---
  const getVerticalStacks = (weekEvents) => {
      const sorted = [...weekEvents].sort((a, b) => {
          if (a.start.getTime() !== b.start.getTime()) return a.start - b.start;
          return (b.end - b.start) - (a.end - a.start);
      });

      const lanes = [];

      const stackedEvents = sorted.map(event => {
          let laneIndex = lanes.findIndex(laneEndDate => isAfter(event.start, laneEndDate));
          
          if (laneIndex === -1) {
              lanes.push(event.end);
              laneIndex = lanes.length - 1;
          } else {
              lanes[laneIndex] = event.end;
          }

          return { ...event, laneIndex };
      });

      return stackedEvents;
  };

  const getEventStyleForWeek = (event, weekDays) => {
      const weekStart = weekDays[0];
      const weekEnd = weekDays[6];

      if (isAfter(event.start, weekEnd) || isAfter(weekStart, event.end)) return null;

      let startIndex = 0;
      let endIndex = 6;

      if (isAfter(event.start, weekStart)) {
          startIndex = differenceInDays(event.start, weekStart);
      }
      
      if (isAfter(weekEnd, event.end)) {
          endIndex = differenceInDays(event.end, weekStart);
      }

      const duration = endIndex - startIndex + 1;

      return {
          left: `${(startIndex / 7) * 100}%`,
          width: `${(duration / 7) * 100}%`,
          top: `${(event.laneIndex * 28) + 32}px` 
      };
  };

  // --- New Logic: Day Intensity (Heatmap) ---
  const getDayIntensity = (day) => {
      const activeCount = processedEvents.filter(e => 
          isValid(e.start) && isValid(e.end) && 
          isWithinInterval(day, { start: e.start, end: e.end })
      ).length;
      
      if (activeCount === 0) return '';
      if (activeCount === 1) return 'bg-blue-900/10';
      if (activeCount === 2) return 'bg-indigo-900/20';
      return 'bg-purple-900/30';
  };

  if (loading) {
    return (
        <div className="h-full w-full flex flex-col items-center justify-center text-slate-500 bg-slate-950">
            <Loader2 size={48} className="animate-spin mb-4 text-slate-400" />
            <p className="font-mono text-xs uppercase tracking-widest text-slate-600">Syncing Schedule...</p>
        </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-950 text-slate-300 font-sans overflow-hidden">
        
        {/* HEADER SECTION */}
        <header className="px-8 py-6 border-b border-slate-700 bg-slate-950/95 z-20">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h1 className="text-3xl font-bold text-white tracking-tight">Campaign Simulation Calendar</h1>
                    <p className="text-slate-400 text-sm mt-1">Strategic intensity mapping and conflict detection</p>
                </div>
                
                <div className="flex items-center space-x-4 bg-slate-800 p-1 rounded-lg border border-slate-700">
                    <button onClick={prevMonth} className="p-2 hover:bg-slate-700 rounded-md text-slate-400 hover:text-white transition-colors">
                        <ChevronLeft size={20} />
                    </button>
                    <div className="px-4 font-mono font-bold text-white w-32 text-center">
                        {format(currentDate, 'MMMM yyyy')}
                    </div>
                    <button onClick={nextMonth} className="p-2 hover:bg-slate-700 rounded-md text-slate-400 hover:text-white transition-colors">
                        <ChevronRight size={20} />
                    </button>
                    <div className="w-px h-6 bg-slate-700 mx-2"></div>
                    <button onClick={goToToday} className="px-3 py-1 text-xs font-bold text-accent-400 hover:bg-slate-700 rounded uppercase tracking-wider">
                        Today
                    </button>
                </div>
            </div>

            <ResourceHeader currentMonthData={processedEvents.filter(e => isValid(e.start) && isSameMonth(e.start, currentDate))} />

            {/* ACTION TOOLBAR */}
            <div className="flex items-center gap-4">
                <div className="relative flex-grow">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                    <input 
                        type="text" 
                        placeholder="Search campaigns..." 
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="w-full bg-slate-800 border border-slate-700 rounded-lg pl-10 pr-4 py-2.5 text-sm text-white focus:outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 transition-all"
                    />
                </div>
                
                <div className="flex items-center gap-2">
                    <div className="bg-slate-800 border border-slate-700 rounded-lg p-2.5 text-slate-500">
                         <Filter size={16} />
                    </div>
                    <select 
                        value={focusFilter}
                        onChange={(e) => setFocusFilter(e.target.value)}
                        className="bg-slate-800 border border-slate-700 rounded-lg px-3 py-2.5 text-sm text-white focus:outline-none focus:border-slate-500 cursor-pointer"
                    >
                        <option value="ALL">All Strategies</option>
                        {Object.entries(CAMPAIGN_FOCUS_TYPES).map(([key, val]) => (
                            <option key={key} value={key}>{val.label}</option>
                        ))}
                    </select>

                    <div className="relative w-48 group">
                         <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-slate-500">
                             <MapPin size={16} />
                         </div>
                         <input 
                             type="text" 
                             placeholder="Find Ward..." 
                             value={wardFilter}
                             onChange={(e) => setWardFilter(e.target.value)}
                             className="w-full pl-10 pr-4 py-2.5 bg-slate-800 border border-slate-700 text-slate-100 rounded-lg text-sm shadow-sm placeholder-slate-500 focus:outline-none focus:border-slate-500"
                         />
                    </div>
                </div>

                <button
                    onClick={onStartCampaign}
                    className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-lg text-sm font-semibold text-slate-100 bg-slate-700 hover:bg-slate-600 transition duration-300 border border-transparent whitespace-nowrap"
                >
                    <Plus size={16} />
                    <span>New Simulation</span>
                </button>
            </div>
        </header>

        {/* CALENDAR GRID */}
        <div className="flex-grow overflow-auto p-8 pt-4">
            
            <div className="grid grid-cols-7 border-b border-slate-800 pb-2 mb-2">
                {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map(day => (
                    <div key={day} className={`text-center text-xs font-bold uppercase tracking-wider ${['Sat', 'Sun'].includes(day) ? 'text-accent-400' : 'text-slate-500'}`}>
                        {day}
                    </div>
                ))}
            </div>

            {/* Weeks Render Loop */}
            <div className="flex flex-col">
                {weeks.map((weekDays, weekIdx) => {
                    const rowHeight = 160; 

                    const weekEvents = processedEvents.filter(event => 
                         areIntervalsOverlapping(
                             { start: event.start, end: event.end },
                             { start: weekDays[0], end: weekDays[6] }
                         )
                    );

                    const stackedEvents = getVerticalStacks(weekEvents);

                    return (
                        <div key={weekIdx} className="relative border-b border-slate-800/50" style={{ height: `${rowHeight}px` }}>
                            
                            {/* Grid Background Cells */}
                            <div className="absolute inset-0 grid grid-cols-7 h-full">
                                {weekDays.map((day, dIdx) => {
                                    const isToday = isSameDay(day, new Date());
                                    const isCurrentMonth = isSameMonth(day, currentDate);
                                    const intensityClass = getDayIntensity(day);
                                    
                                    return (
                                        <div 
                                            key={day.toString()} 
                                            onClick={() => onStartCampaign && onStartCampaign(day)}
                                            className={`
                                                relative border-r border-slate-800/30 p-2 transition-colors cursor-pointer hover:bg-slate-800/30
                                                ${!isCurrentMonth ? 'bg-slate-900/30' : ''}
                                                ${isToday ? 'bg-slate-800/20' : ''}
                                                ${intensityClass}
                                            `}
                                        >
                                            <span className={`text-sm font-bold ${isToday ? 'text-white bg-accent-500 px-1.5 py-0.5 rounded' : 'text-slate-500'}`}>
                                                {format(day, 'd')}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Events Layer */}
                            <div className="absolute inset-x-0 top-0 bottom-0 pointer-events-none">
                                {stackedEvents.map((event) => {
                                    const style = CAMPAIGN_FOCUS_TYPES[event.campaign_focus] || CAMPAIGN_FOCUS_TYPES.ISSUE_BASED_PERSUASION;
                                    const position = getEventStyleForWeek(event, weekDays);
                                    
                                    if (!position) return null;

                                    return (
                                        <div 
                                            key={event.id}
                                            onClick={(e) => {
                                                e.stopPropagation(); 
                                                setSelectedEvent(event);
                                            }}
                                            className={`
                                                pointer-events-auto cursor-pointer absolute h-6 text-[10px] font-medium flex items-center justify-between shadow-sm z-10
                                                ${style.color} hover:brightness-110 transition-all border-r border-slate-900/20
                                                ${event.conflictDetected ? 'ring-2 ring-red-500' : ''}
                                            `}
                                            style={{ 
                                                left: position.left, 
                                                width: position.width,
                                                top: position.top,
                                                borderRadius: '2px' 
                                            }}
                                        >
                                            <span className="truncate text-white drop-shadow-md px-1">
                                                {event.is_pulse_campaign && "⚡ "}{event.campaign_name}
                                            </span>
                                            {event.conflictDetected && <AlertTriangle size={10} className="text-white mr-1" />}
                                        </div>
                                    );
                                })}
                            </div>

                        </div>
                    );
                })}
            </div>
        </div>

        {/* SIDEBAR & BACKDROP */}
        {selectedEvent && (
            <EventSidebar 
                event={selectedEvent} 
                onClose={() => setSelectedEvent(null)}
                setActiveView={setActiveView}
            />
        )}
        
        {selectedEvent && (
            <div 
                className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity"
                onClick={() => setSelectedEvent(null)}
            ></div>
        )}

    </div>
  );
};

export default SimCalendar;